/**
 * SlackOAuthConnect Components
 *
 * One-click "Add to Slack" button for OAuth-based Slack integration.
 * Handles OAuth flow initiation and displays connected workspaces.
 *
 * Design Compliance:
 * - Follows Polaris button and layout patterns
 * - Uses official Slack branding guidelines
 * - Accessible keyboard navigation
 */

import { useState, useCallback, useEffect } from 'react';
import { authenticatedFetch } from '~/utils/authenticated-fetch';
import { logger } from '~/utils/client-logger';
import {
  BlockStack,
  InlineStack,
  Text,
  Button,
  Box,
  Badge,
  Spinner,
  Banner,
  Modal,
} from '@shopify/polaris';
import {
  DeleteIcon,
  RefreshIcon,
  CheckIcon,
  XIcon,
} from '@shopify/polaris-icons';
import type { SlackInstallationPublic } from '../../../../src/types';

/**
 * H2 FIX: Use centralized SlackInstallationPublic type from src/types
 * This ensures consistent typing across frontend and backend.
 * Re-export as SlackInstallation for backward compatibility with existing consumers.
 */
export type SlackInstallation = SlackInstallationPublic;

/**
 * Slack channel for selection
 */
export interface SlackChannel {
  id: string;
  name: string;
  is_private: boolean;
  is_member: boolean;
}

/**
 * API Response interfaces
 */
interface InstallationsApiResponse {
  success: boolean;
  installations?: SlackInstallation[];
  error?: string;
}

interface OAuthUrlApiResponse {
  success: boolean;
  url?: string;
  error?: string;
}

interface TestApiResponse {
  success: boolean;
  error?: string;
}

interface DisconnectApiResponse {
  success: boolean;
  error?: string;
}

export interface SlackOAuthConnectProps {
  /** Current shop domain */
  shopDomain: string;
  /** Callback when installation list changes */
  onInstallationsChange?: () => void;
  /** Callback to show toast messages */
  onToast?: (message: string, isError?: boolean) => void;
}

// Slack logo SVG component (official brand asset)
export function SlackLogo({ size = 20 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zM6.313 15.165a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313z"
        fill="#E01E5A"
      />
      <path
        d="M8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zM8.834 6.313a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312z"
        fill="#36C5F0"
      />
      <path
        d="M18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zM17.688 8.834a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.523 2.522v6.312z"
        fill="#2EB67D"
      />
      <path
        d="M15.165 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zM15.165 17.688a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523h-6.313z"
        fill="#ECB22E"
      />
    </svg>
  );
}

/**
 * Hook to manage Slack installations
 */
export function useSlackInstallations(shopDomain: string, onInstallationsChange?: () => void, onToast?: (msg: string, isError?: boolean) => void) {
  const [installations, setInstallations] = useState<SlackInstallation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [testingId, setTestingId] = useState<number | null>(null);
  const [disconnectingId, setDisconnectingId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchInstallations = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await authenticatedFetch(`/api/slack/installations?shop=${encodeURIComponent(shopDomain)}`);
      const data: InstallationsApiResponse = await response.json();

      if (data.success) {
        setInstallations(data.installations || []);
      } else {
        setError(data.error || 'Failed to load Slack connections');
      }
    } catch (err) {
      setError('Failed to load Slack connections');
      logger.error('SlackOAuthConnect', 'Error fetching installations', err);
    } finally {
      setIsLoading(false);
    }
  }, [shopDomain]);

  useEffect(() => {
    fetchInstallations();
  }, [fetchInstallations]);

  const handleTest = useCallback(async (installation: SlackInstallation) => {
    try {
      setTestingId(installation.id);

      const response = await authenticatedFetch(`/api/slack/installation/${installation.id}/test?shop=${encodeURIComponent(shopDomain)}`, {
        method: 'POST',
      });
      const data: TestApiResponse = await response.json();

      if (data.success) {
        onToast?.('Test message sent successfully', false);
      } else {
        onToast?.(data.error || 'Failed to send test message', true);
      }
    } catch (err) {
      onToast?.('Failed to send test message', true);
      logger.error('SlackOAuthConnect', 'Error testing installation', err);
    } finally {
      setTestingId(null);
    }
  }, [shopDomain, onToast]);

  const handleDisconnect = useCallback(async (installation: SlackInstallation) => {
    try {
      setDisconnectingId(installation.id);

      const response = await authenticatedFetch(`/api/slack/installation/${installation.id}?shop=${encodeURIComponent(shopDomain)}`, {
        method: 'DELETE',
      });
      const data: DisconnectApiResponse = await response.json();

      if (data.success) {
        onToast?.(`Disconnected from ${installation.team_name}`, false);
        await fetchInstallations();
        onInstallationsChange?.();
        return true;
      } else {
        onToast?.(data.error || 'Failed to disconnect', true);
        return false;
      }
    } catch (err) {
      onToast?.('Failed to disconnect from Slack', true);
      logger.error('SlackOAuthConnect', 'Error disconnecting', err);
      return false;
    } finally {
      setDisconnectingId(null);
    }
  }, [shopDomain, fetchInstallations, onInstallationsChange, onToast]);

  return {
    installations,
    isLoading,
    error,
    setError,
    fetchInstallations,
    handleTest,
    handleDisconnect,
    testingId,
    disconnectingId,
  };
}

export function SlackConnectButton({
  shopDomain,
  onSuccess,
  onToast,
}: {
  shopDomain: string;
  onSuccess?: () => void;
  onToast?: (msg: string, isError?: boolean) => void;
}) {
  const [isConnecting, setIsConnecting] = useState(false);

  const handleAddToSlack = useCallback(async () => {
    try {
      setIsConnecting(true);

      // Get OAuth URL from backend
      const response = await authenticatedFetch(`/api/slack/oauth/url?shop=${encodeURIComponent(shopDomain)}`);
      const data: OAuthUrlApiResponse = await response.json();

      if (data.success && data.url) {
        // Open Slack OAuth in a popup
        const width = 600;
        const height = 700;
        const left = window.screenX + (window.innerWidth - width) / 2;
        const top = window.screenY + (window.innerHeight - height) / 2;

        const popup = window.open(
          data.url,
          'slack-oauth',
          `width=${width},height=${height},left=${left},top=${top},scrollbars=yes`
        );

        // Poll for popup close (OAuth completion)
        const pollInterval = setInterval(() => {
          if (popup?.closed) {
            clearInterval(pollInterval);
            setIsConnecting(false);
            onSuccess?.();
          }
        }, 500);

        // Timeout after 5 minutes
        setTimeout(() => {
          clearInterval(pollInterval);
          setIsConnecting(false);
        }, 300000);
      } else {
        onToast?.(data.error || 'Failed to initiate Slack connection', true);
        setIsConnecting(false);
      }
    } catch (err) {
      onToast?.('Failed to connect to Slack', true);
      setIsConnecting(false);
      logger.error('SlackOAuthConnect', 'Error initiating OAuth', err);
    }
  }, [shopDomain, onSuccess, onToast]);

  return (
    <Button
      size="slim"
      onClick={handleAddToSlack}
      loading={isConnecting}
      icon={
        <span style={{ display: 'inline-flex', alignItems: 'center', padding: '0 6px' }}>
          <SlackLogo size={18} />
        </span>
      }
    >
      Add to Slack
    </Button>
  );
}

export function SlackInstallationsList({
  installations,
  isLoading,
  error,
  onErrorDismiss,
  testingId,
  disconnectingId,
  onTest,
  onDisconnect,
  hideEmptyState = false,
}: {
  installations: SlackInstallation[];
  isLoading: boolean;
  error: string | null;
  onErrorDismiss: () => void;
  testingId: number | null;
  disconnectingId: number | null;
  onTest: (installation: SlackInstallation) => void;
  onDisconnect: (installation: SlackInstallation) => void;
  hideEmptyState?: boolean;
}) {
  const [showDisconnectModal, setShowDisconnectModal] = useState(false);
  const [selectedInstallation, setSelectedInstallation] = useState<SlackInstallation | null>(null);

  const handleDisconnectClick = useCallback((installation: SlackInstallation) => {
    setSelectedInstallation(installation);
    setShowDisconnectModal(true);
  }, []);

  const handleDisconnectConfirm = useCallback(async () => {
    if (selectedInstallation) {
      onDisconnect(selectedInstallation);
      setShowDisconnectModal(false);
      setSelectedInstallation(null);
    }
  }, [selectedInstallation, onDisconnect]);

  /**
   * H1 FIX: Use locale-independent date formatting to prevent SSR/client hydration mismatch.
   */
  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  if (isLoading) {
    return (
      <Box padding="400">
        <InlineStack align="center" gap="200">
          <Spinner size="small" />
          <Text as="span" variant="bodySm" tone="subdued">
            Loading Slack connections...
          </Text>
        </InlineStack>
      </Box>
    );
  }

  if (installations.length === 0 && hideEmptyState) {
    return null;
  }

  return (
    <>
      <BlockStack gap="400">
        {error && (
          <Banner tone="critical" onDismiss={onErrorDismiss}>
            {error}
          </Banner>
        )}

        {/* Connected Workspaces */}
        {installations.length > 0 ? (
          <BlockStack gap="300">
            <Text as="h3" variant="headingSm">
              Connected Workspaces
            </Text>
            {installations.map((installation) => (
              <Box
                key={installation.id}
                background="bg-surface-secondary"
                padding="300"
                borderRadius="200"
              >
                <InlineStack align="space-between" blockAlign="center">
                  <InlineStack gap="300" blockAlign="center">
                    <SlackLogo size={24} />
                    <BlockStack gap="100">
                      <InlineStack gap="200" blockAlign="center">
                        <Text as="span" variant="bodyMd" fontWeight="semibold">
                          {installation.team_name}
                        </Text>
                        {installation.is_active ? (
                          <Badge tone="success" icon={CheckIcon}>Active</Badge>
                        ) : (
                          <Badge tone="critical" icon={XIcon}>Inactive</Badge>
                        )}
                      </InlineStack>
                      <Text as="span" variant="bodySm" tone="subdued">
                        {installation.incoming_webhook_channel
                          ? `Channel: #${installation.incoming_webhook_channel}`
                          : 'No default channel'}
                        {' | '}
                        Last used: <span suppressHydrationWarning>{formatDate(installation.last_used_at)}</span>
                      </Text>
                    </BlockStack>
                  </InlineStack>
                  <InlineStack gap="200">
                    <Button
                      size="slim"
                      icon={RefreshIcon}
                      onClick={() => onTest(installation)}
                      loading={testingId === installation.id}
                      disabled={!installation.is_active}
                    >
                      Test
                    </Button>
                    <Button
                      size="slim"
                      tone="critical"
                      icon={DeleteIcon}
                      onClick={() => handleDisconnectClick(installation)}
                      loading={disconnectingId === installation.id}
                    >
                      Disconnect
                    </Button>
                  </InlineStack>
                </InlineStack>
              </Box>
            ))}
          </BlockStack>
        ) : (
          <Box
            background="bg-surface-secondary"
            padding="400"
            borderRadius="200"
          >
            <BlockStack gap="200" align="center">
              <Text as="p" variant="bodyMd" tone="subdued" alignment="center">
                No Slack workspaces connected.
              </Text>
              <Text as="p" variant="bodySm" tone="subdued" alignment="center">
                Click "Add to Slack" above to connect your workspace.
              </Text>
            </BlockStack>
          </Box>
        )}
      </BlockStack>

      {/* Disconnect Confirmation Modal */}
      <Modal
        open={showDisconnectModal}
        onClose={() => {
          setShowDisconnectModal(false);
          setSelectedInstallation(null);
        }}
        title="Disconnect Slack workspace?"
        primaryAction={{
          content: 'Disconnect',
          destructive: true,
          onAction: handleDisconnectConfirm,
          loading: disconnectingId !== null,
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => {
              setShowDisconnectModal(false);
              setSelectedInstallation(null);
            },
          },
        ]}
      >
        <Modal.Section>
          <Text as="p" variant="bodyMd">
            Are you sure you want to disconnect <strong>{selectedInstallation?.team_name}</strong>?
            You will no longer receive stock alerts in this workspace.
          </Text>
        </Modal.Section>
      </Modal>
    </>
  );
}

export function SlackOAuthConnect(props: SlackOAuthConnectProps) {
  const {
    installations,
    isLoading,
    error,
    setError,
    fetchInstallations,
    handleTest,
    handleDisconnect,
    testingId,
    disconnectingId,
  } = useSlackInstallations(props.shopDomain, props.onInstallationsChange, props.onToast);

  return (
    <BlockStack gap="400">
      <SlackInstallationsList
        installations={installations}
        isLoading={isLoading}
        error={error}
        onErrorDismiss={() => setError(null)}
        testingId={testingId}
        disconnectingId={disconnectingId}
        onTest={handleTest}
        onDisconnect={handleDisconnect}
      />
      <Box paddingBlockStart={installations.length > 0 ? "200" : "0"}>
         <InlineStack align="start">
            <SlackConnectButton
              shopDomain={props.shopDomain}
              onSuccess={() => {
                 fetchInstallations();
                 props.onInstallationsChange?.();
              }}
              onToast={props.onToast}
            />
         </InlineStack>
         <Box paddingBlockStart="200">
            <Text as="p" variant="bodySm" tone="subdued">
              Connect your Slack workspace to receive stock alerts directly in your channels.
            </Text>
         </Box>
      </Box>
    </BlockStack>
  );
}